import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { nameModelSortFn, type ProcedureType, type Stored } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { procedureTypeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	ProcedureTypeSelectGroup,
	type ProcedureTypeSelectGroupProps,
} from "../MenuSelectGroups/ProcedureTypeSelectGroup"

type ProcedureTypeDropdownSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	ProcedureTypeSelectGroupProps & {
		children: ReactElement
	}

export const ProcedureTypeDropdownSelect = memo<ProcedureTypeDropdownSelectProps>((props) => {
	const {
		disabled = false,
		children,
		radius,
		size,
		variant,
		accentColor,
		procedureTypes,
		required,
		defaultValue,
		value,
		onValueChange,
		...rest
	} = props

	const sortedProcedureTypes = useSort(procedureTypes, nameModelSortFn)

	const [filteredProcedureTypes, filterValue, setFilterValue] = useTextFilter<Stored<ProcedureType>>(
		sortedProcedureTypes,
		procedureTypeFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredProcedureTypes.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<ProcedureTypeSelectGroup
							required={required}
							defaultValue={defaultValue}
							value={value}
							onValueChange={onValueChange}
							procedureTypes={filteredProcedureTypes}
						/>
					) : (
						<ProcedureTypeSelectGroup
							required={required}
							defaultValue={defaultValue}
							value={value}
							onValueChange={onValueChange}
							procedureTypes={filteredProcedureTypes}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})

ProcedureTypeDropdownSelect.displayName = "ProcedureTypeDropdownSelect"
